import React, { Component } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';

/**
 * 增强版错误边界组件，用于捕获子组件中的错误并显示回退UI
 * 特性:
 * - 支持Material UI样式
 * - 支持重试功能
 * - 支持自动重试
 * - 可传入自定义回退UI
 * - 错误计数和限制重试次数
 * 
 * 使用方法:
 * <ErrorBoundary 
 *   fallback={<CustomErrorUI />}
 *   onError={(error) => console.log('捕获到错误:', error)}
 *   autoRetry={true}
 *   retryDelay={2000}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // 更新状态，下一次渲染将显示后备UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // 记录错误信息
    this.setState({ errorInfo });
    console.error('ErrorBoundary捕获到错误:', error);
    console.error('错误组件堆栈:', errorInfo?.componentStack);
    
    // 检查是否与PayPal相关
    const isPayPalError = error.message?.includes('paypal') || 
                           error.message?.includes('PayPal') ||
                           errorInfo?.componentStack?.includes('PayPal');
                           
    if (isPayPalError) {
      console.warn('检测到PayPal相关错误，将尝试自动恢复');
    }

    // 如果启用了自动重试，则在一段时间后重置错误状态
    if (this.props.autoRetry && this.state.retryCount < (this.props.maxRetries || 3)) {
      const delay = this.props.retryDelay || 2000;
      setTimeout(() => {
        console.log(`尝试恢复错误组件 (${this.state.retryCount + 1}/${this.props.maxRetries || 3})`);
        this.setState(prevState => ({ 
          hasError: false, 
          error: null, 
          errorInfo: null,
          retryCount: prevState.retryCount + 1
        }));
      }, delay);
    }
  }

  handleManualRetry = () => {
    console.log('手动重试组件渲染');
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // 检查是否提供了fallback渲染函数或组件
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback({
            error: this.state.error,
            errorInfo: this.state.errorInfo,
            retry: this.handleManualRetry
          });
        }
        return this.props.fallback;
      }

      // 默认错误UI
      return (
        <div style={{ 
          padding: '20px', 
          margin: '20px',
          backgroundColor: '#fff8f8',
          border: '1px solid #ffcdd2',
          borderRadius: '4px'
        }}>
          <h2 style={{ color: '#d32f2f' }}>组件发生错误</h2>
          <p>抱歉，此组件加载失败。</p>
          {this.props.showDetails && (
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '16px' }}>
              <summary>错误详情</summary>
              <p>{this.state.error && this.state.error.toString()}</p>
              <p>{this.state.errorInfo && this.state.errorInfo.componentStack}</p>
            </details>
          )}
          <button 
            onClick={this.handleManualRetry}
            style={{
              padding: '8px 16px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '12px'
            }}
          >
            重试
          </button>
        </div>
      );
    }

    return this.props.children; 
  }
}

export default ErrorBoundary;